import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface RecommendationRow {
  readonly recommendationId: string;
  readonly recommendationType: string;
  readonly entityType: string;
  readonly recommendedOption: string;
  readonly confidenceScore: string;
  readonly status: string;
}

const RECOMMENDATION_STATUSES = ['', 'ISSUED', 'ACCEPTED', 'REJECTED', 'EXPIRED'];

/**
 * M25 — Recommendation Center (§12).
 * Lists ranked advisory recommendations (vendor selection, budget allocation,
 * routing, assignment, …) with the top-ranked option and confidence. Every
 * recommendation stays ISSUED until a human accepts or rejects it (BR-01).
 */
export function M25RecommendationCenterPage(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<RecommendationRow[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback((): void => {
    setLoading(true);
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    apiRequest<{ data: RecommendationRow[]; total: number }>(`/v1/ai/recommendations${query}`, { token })
      .then((r) => { setRows(r.data); setTotal(r.total); setError(null); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token, status]);

  useEffect(() => { load(); }, [load]);

  function decide(recommendationId: string, decision: 'ACCEPTED' | 'REJECTED'): void {
    const decisionReason = decision === 'REJECTED'
      ? window.prompt(t('m25.recommendation.rejectReason'))
      : undefined;
    if (decision === 'REJECTED' && (decisionReason == null || decisionReason.trim().length === 0)) return;
    setBusy(true);
    setNotice(null);
    setError(null);
    apiRequest(`/v1/ai/recommendations/${encodeURIComponent(recommendationId)}/decide`, {
      method: 'POST',
      token,
      body: { status: decision, decisionReason: decisionReason?.trim() },
    })
      .then(() => { setNotice(decision === 'ACCEPTED' ? t('m25.recommendation.accepted') : t('m25.recommendation.rejected')); load(); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setBusy(false));
  }

  return (
    <main aria-label="recommendation-center">
      <h1>{t('m25.recommendation.title')}</h1>
      <label htmlFor="recommendation-status-filter">{t('m25.recommendation.filterLabel')}</label>
      <select id="recommendation-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}>
        {RECOMMENDATION_STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m25.recommendation.allStatuses')}</option>)}
      </select>
      {notice && <p role="status">{notice}</p>}
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m25.recommendation.loading')}</p> : (
        <>
          <p aria-label="recommendation-count">{t('m25.recommendation.count', { count: total })}</p>
          <table aria-label="recommendation-table">
            <thead>
              <tr><th>{t('m25.recommendation.colType')}</th><th>{t('m25.recommendation.colEntity')}</th><th>{t('m25.recommendation.colRecommended')}</th><th>{t('m25.recommendation.colConfidence')}</th><th>{t('m25.recommendation.colStatus')}</th><th>{t('m25.recommendation.colDecision')}</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const decided = r.status === 'ACCEPTED' || r.status === 'REJECTED' || r.status === 'EXPIRED';
                return (
                  <tr key={r.recommendationId}>
                    <td>{r.recommendationType}</td>
                    <td>{r.entityType}</td>
                    <td>{r.recommendedOption}</td>
                    <td>{decimal(r.confidenceScore)}</td>
                    <td>{r.status}</td>
                    <td>
                      <button type="button" aria-label={`accept-${r.recommendationId}`} disabled={busy || decided} onClick={() => decide(r.recommendationId, 'ACCEPTED')}>{t('m25.recommendation.accept')}</button>
                      <button type="button" aria-label={`reject-${r.recommendationId}`} disabled={busy || decided} onClick={() => decide(r.recommendationId, 'REJECTED')}>{t('m25.recommendation.reject')}</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
